import './ContactForm.less';

import axios from 'axios';
import clsx from 'clsx';
import { Map, fromJS } from 'immutable';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { handleValidForm, makeField, makeForm, validator } from 'nike-form-js';
import { dispatch, useReframe } from 'nike-re-framejs';

import Input from '../forms/Input';
import i18n from '../../common/i18n';
import { loggedIn$ } from '../turboLive/subs';
import { DirectFileUploadInput, DocumentUploadInput } from '../dms/DocumentUploadInput';
import { contactUsSlots$, fetchContactUsSlots } from './h&s';
import PhoneNumberInput, { getPhoneNumberValidator } from '../forms/PhoneNumberInput';
import { compoundValidator } from '../registration/util/validations';
import { isProfileContactTopicEnabled } from '../../profiles';
import { contractNumber$, email$, firstName$, lastName$, telephone$ } from '../../common/h&s';
import TextArea from '../forms/TextArea';
import { removeUploadFile, selectDocument } from '../registration-completion/view/step_identity_check/h&s';
import Submit from '../forms/Submit';
import { LoaderOrangeRings } from '../loaders/loaderOrangeRings/LoaderOrangeRings';
import { getUploadVersion } from '../dms/h&s';
import FieldErrors from '../forms/FieldErrors';
import { Select } from '../forms/Select';
import { createDialog, DialogProps, openDialog } from '../dialogs/dialogs';
import { nuiCtx } from '../../common/initialization';
import SuccessIcon from '../../icons/SuccessIcon';
import NegativeIcon from '../../icons/NegativeIcon';
import { Button } from '../button/Button';

export function ContactForm({ showContractId }) {
    const { subscribe } = useReframe();
    const loggedIn = subscribe(loggedIn$);
    const [result, setResult] = useState('');
    const formCompleted = result && result !== 'inProgress';
    const attachmentFile1 = useRef<File>();
    const attachmentFile2 = useRef<File>();

    const form = useMemo(() => {
        const phone = subscribe(telephone$());
        return makeForm(
            Map()
                .setIn(PATH.NAME, subscribe(firstName$()))
                .setIn(PATH.SURNAME, subscribe(lastName$()))
                .setIn(PATH.EMAIL, subscribe(email$()))
                .setIn(PATH.PHONE, phone ? phone.substring(4) : null)
                .setIn(PATH.PHONE_PREFIX, '+421')
                .setIn(PATH.CONTRACT_ID, subscribe(contractNumber$()))
                .setIn(PATH.REASON, '-1'),
            {
                validator: validator(
                    contactUsFormValidator,
                    compoundValidator(getPhoneNumberValidator(PATH.PHONE, PATH.PHONE_PREFIX))
                )
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchContactUsSlots();
    }, []);
    const slots = subscribe(contactUsSlots$);

    function submit() {
        return handleValidForm(form, formValues => {
            setResult('inProgress');
            const values = formValues.toJS();
            const formData = new FormData();
            formData.append('firstName', values.name);
            formData.append('lastName', values.surname);
            formData.append('reasonId', values.reason);
            formData.append('text', values.message);
            formData.append('phone', values.phonePrefix + values.phone);
            formData.append('email', values.email);
            formData.append('contractId', values.contractId || '');
            slots.forEach(slot => {
                const doc = slot.documents[0];
                const version = subscribe(getUploadVersion(doc.slotName, doc.dmsId, 1));
                if (version) {
                    formData.append('docVersions', version);
                }
            });
            formData.append('attachmentFile1', attachmentFile1.current);
            formData.append('attachmentFile2', attachmentFile2.current);
            axios
                .post('/api/contact-us', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                })
                .then(r => {
                    /* @ts-ignore */
                    setResult(r.code);
                    openDialog(ContactFormResultDialog, { success: true });
                })
                .catch(() => {
                    openDialog(ContactFormResultDialog, { success: false });
                });
        });
    }

    return (
        <form onSubmit={submit()} className="contact-form">
            <h1>{i18n('nui.contactUs.title')}</h1>
            <div className="mb-20 c-black-50-2 fs-14">{i18n('nui.contactUs.description')}</div>
            <Input
                form={form}
                path={PATH.NAME}
                label={i18n('nui.contactUs.name')}
                autocomplete="name"
                disabled={loggedIn}
            />
            <Input
                form={form}
                path={PATH.SURNAME}
                label={i18n('nui.contactUs.surname')}
                autocomplete="surname"
                disabled={loggedIn}
            />
            <Input
                form={form}
                path={PATH.EMAIL}
                label={i18n('nui.contactUs.email')}
                autocomplete="email"
                disabled={loggedIn}
            />
            <PhoneNumberInput
                form={form}
                path={PATH.PHONE}
                prefixPath={PATH.PHONE_PREFIX}
                label={i18n('nui.contactUs.phone')}
                disabled={loggedIn}
            />
            {showContractId ? (
                <Input
                    form={form}
                    path={PATH.CONTRACT_ID}
                    label={i18n('nui.contactUs.contractId')}
                    disabled={loggedIn}
                    optional
                />
            ) : null}
            {isProfileContactTopicEnabled() && <ReasonDropDown form={form} />}
            <TextArea
                /* @ts-ignore */
                form={form}
                path={PATH.MESSAGE}
                className="mt-15"
                label={i18n('nui.contactUs.message')}
            />
            {loggedIn ? (
                slots.map((slot, i) => {
                    const doc = slot.documents[0];
                    const slotDescription = {
                        slot: doc.slotName,
                        idSlot: doc.slotName,
                        idDocDms: doc.dmsId,
                        page: 1 /* slot.pages */,
                        clientHash: doc.clientHash
                    };
                    return (
                        <div key={doc.dmsId}>
                            <FormLabel label={i18n('nui.attachment') + ' ' + (i + 1)} optional />
                            <DocumentUploadInput
                                slotDescription={slotDescription}
                                placeHolderText={i18n('nui.contactUs.attachment')}
                                style="STRIP"
                                beforeUpload={() => dispatch(selectDocument(doc.slotName, fromJS(slotDescription)))}
                                formCompleted={formCompleted}
                                deleteIcon
                                removeUploadFile={() => dispatch(removeUploadFile(slotDescription))}
                            />
                        </div>
                    );
                })
            ) : (
                <>
                    <FormLabel label={i18n('nui.attachment') + ' 1'} optional />
                    <DirectFileUploadInput
                        ref={attachmentFile1}
                        style="STRIP"
                        placeholderText={i18n('nui.contactUs.attachment')}
                        formCompleted={formCompleted}
                    />
                    <FormLabel label={i18n('nui.attachment') + ' 2'} optional />
                    <DirectFileUploadInput
                        ref={attachmentFile2}
                        style="STRIP"
                        placeholderText={i18n('nui.contactUs.attachment')}
                        formCompleted={formCompleted}
                    />
                </>
            )}
            <div className="mt-30">
                <SubmitOrMessage submitResult={result} form={form} />
            </div>
        </form>
    );
}

const ContactFormResultDialog = createDialog({
    name: 'nui-contact-form-result-dialog',
    component: ({ success }: { success?: boolean } & DialogProps) => {
        const { NuiDialog } = nuiCtx.components;
        return (
            <NuiDialog
                hideCloseButton
                name="nui-contact-form-result-dialog"
                className={clsx({ 'contact-form-result-dialog-portal': nuiCtx.platform === 'portal' })}
                onClose={() => window.location.reload()}
            >
                <div className="contact-form-result-dialog-content">
                    <div className="text-center mt-30">
                        {success ? <SuccessIcon width={70} height={70} /> : <NegativeIcon width={70} height={70} />}
                    </div>
                    <h1 className="text-center uppercase fs-18 py-20">
                        {success ? i18n('nui.contactUs.ok') : i18n('nui.contactUs.error')}
                    </h1>
                    <Button
                        onClick={() => window.location.reload()}
                        primary
                        large
                        className="nui-button full-width mt-30"
                        label={i18n('nui.button.close')}
                        dataAtid="button-contact-form-result-close"
                    />
                </div>
            </NuiDialog>
        );
    }
});

const FormLabel = ({ label, optional }: { label: string; optional?: boolean }) => (
    <div className="nui-input-label mt-15 mb-5">
        {optional && <span className="pull-right c-black-50-2 fs-12">{i18n('nui.form.optional')}</span>}
        <span className="text-extra-bold fs-14">{label}</span>
    </div>
);

function ReasonDropDown({ form }) {
    const [categories, setCategories] = useState<Array<[string, string]>>([]);
    useEffect(() => {
        if (isProfileContactTopicEnabled()) {
            axios
                .get('/api/contact-us/categories')
                .then(types => {
                    /* @ts-ignore */
                    let categories = types.map(c => [c.categories[0].id, c.name]);
                    setCategories(categories);
                    categoriesAvailable = categories && categories.length > 0;
                })
                .catch(() => {
                    setCategories([]);
                    categoriesAvailable = false;
                });
        }
    }, []);

    const choices = [[-1, i18n('nui.contactUs.choose') + '...']].concat(categories || []);
    const reasonField = makeField(form, PATH.REASON, undefined);
    const { deref } = useReframe();
    const errors = deref(reasonField.getErrors());
    const isTouched = deref(reasonField.isTouched());
    const isError = isTouched && errors.size > 0;

    if (!categories?.length) {
        return null;
    }

    return (
        <>
            <FormLabel label={i18n('nui.contactUs.reason')} />
            {/*@ts-ignore*/}
            <Select field={reasonField} options={choices.map(c => ({ value: '' + c[0], label: c[1] }))} />
            {isError ? <FieldErrors errors={errors} className /> : null}
        </>
    );
}

function SubmitOrMessage({ submitResult, form }) {
    if (submitResult === 'inProgress') {
        return <LoaderOrangeRings />;
    }
    return (
        <div className="flex-center">
            {/* @ts-ignore */}
            <Submit
                disabled={submitResult === 'inProgress' || submitResult === 'ok'}
                form={form}
                label={i18n('nui.button.send')}
                className="contact-form-submit"
            />
        </div>
    );
}

export const PATH = {
    NAME: ['name'],
    SURNAME: ['surname'],
    EMAIL: ['email'],
    PHONE_PREFIX: ['phonePrefix'],
    PHONE: ['phone'],
    CONTRACT_ID: ['contractId'],
    MESSAGE: ['message'],
    REASON: ['reason']
};

const contactUsFormValidator = [
    [
        PATH.NAME,
        value =>
            typeof value !== 'undefined' && /^[a-zA-Z\u00C0-\u017F\s]{1,50}$/.test(value)
                ? null
                : i18n('nui.contactUs.validation.name')
    ],
    [
        PATH.SURNAME,
        value =>
            typeof value !== 'undefined' && /^[a-zA-Z\u00C0-\u017F\s]{1,50}$/.test(value)
                ? null
                : i18n('nui.contactUs.validation.surname')
    ],
    [
        PATH.EMAIL,
        value =>
            typeof value !== 'undefined' &&
            /^[_a-zA-Z0-9-]+(\.[_a-zA-Z0-9-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*(\.[a-zA-Z]{2,4})$/i.test(value)
                ? null
                : i18n('nui.contactUs.validation.email')
    ],
    [
        PATH.MESSAGE,
        value =>
            typeof value !== 'undefined' && /^.{1,1000}$/.test(value.replace(/[\n\r]/g, ''))
                ? null
                : i18n('nui.contactUs.validation.message')
    ],
    [
        PATH.REASON,
        value => {
            return !categoriesAvailable || (value && value !== '-1') ? null : i18n('nui.contactUs.validation.reason');
        }
    ]
];
let categoriesAvailable = false;
